import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

// Small orange eyebrow label with square marker
export const SectionLabel = ({ children, light }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="w-2.5 h-2.5 bg-[#CD0008] rotate-45 inline-block" />
    <span className={`eyebrow ${light ? "text-white/70" : "text-[#CD0008]"}`}>{children}</span>
  </div>
);

// Count-up animation when scrolled into view
export const CountUp = ({ end, suffix = "", duration = 1800, className = "" }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setVal(Math.floor(eased * end));
              if (p < 1) requestAnimationFrame(tick);
              else setVal(end);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} className={className}>
      {val}
      {suffix}
    </span>
  );
};

// Reveal-on-scroll wrapper (supports direction: up|down|left|right|scale)
export const Reveal = ({ children, className = "", delay = 0, direction = "up" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal reveal-${direction} ${visible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

// Reusable orange button
export const OrangeButton = ({ to, href, children, dark, className = "" }) => {
  const base = `inline-flex items-center gap-2 font-display font-semibold uppercase tracking-wide text-sm px-7 py-4 rounded-sm transition-colors ${
    dark
      ? "bg-[#0B102D] hover:bg-black text-white"
      : "bg-[#CD0008] hover:bg-[#A80006] text-white"
  } ${className}`;
  const inner = (
    <>
      {children}
      <span className="flex items-center justify-center w-6 h-6 bg-white/20 rounded-sm">
        <ChevronRight size={16} />
      </span>
    </>
  );
  if (to) return <Link to={to} className={base}>{inner}</Link>;
  return <a href={href} className={base}>{inner}</a>;
};

// Inner page banner / breadcrumb header
export const PageBanner = ({ title, crumb, image }) => (
  <section className="relative pt-40 pb-24 bg-[#0B102D] overflow-hidden">
    <div
      className="absolute inset-0 opacity-25 bg-cover bg-center"
      style={{ backgroundImage: `url(${image})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B102D] via-[#0B102D]/85 to-[#0B102D]/40" />
    <div className="relative max-w-[1320px] mx-auto px-5">
      <SectionLabel light>Salmons Brothers</SectionLabel>
      <h1 className="display-heading text-white text-4xl md:text-6xl mb-4">{title}</h1>
      <div className="flex items-center gap-2 text-white/70 font-display text-sm">
        <Link to="/" className="hover:text-[#CD0008]">Home</Link>
        <ChevronRight size={14} className="text-[#CD0008]" />
        <span className="text-[#CD0008]">{crumb || title}</span>
      </div>
    </div>
  </section>
);
